import BackArrow from '@cashew/back-arrow';
import Loading from '@cashew/loading';
import Title from '@cashew/title';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button, Input, PageWithNavigation } from '../../components';
import { resetKYCAttempts, updateCustomerData } from './customerDetailApi';
import './index.css';
import SelectComponent from '../../components/SelectComponent';
import ResetModal from './ResetModal';
import ValidationModal from './ValidationModal';
import type { CusomersDTOResponseDataType, CustomerDTOType } from './types';
import usePolling from './usePolling';

const { api, dateFormat } = require('@cashew/utils');

const ns = 'customers-details';

/**
 * Orders Page RefundPage
 */

const currencyData = [
  {
    key: '+971',
    value: 'United Arab Emirates +971',
    onSelectedValue: '+971',
    image: '/images/uae.png',
  },
  {
    key: '+966',
    value: 'Saudi Arabia +966',
    onSelectedValue: '+966',
    image: '/images/ksa.png',
  },
];
const requestDelay = 10; // seconds

export type StatusType = 'warning' | 'error' | 'info' | 'success' | 'none';
type ParamsType = { customerId: string; lang: 'en' | 'ar' };
export default () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language, token, role, accessType } = useSelector(
    (state: any) => state.user
  );
  const { customerId }: ParamsType = useParams();
  const [loading, setLoading] = useState(false);
  const [kycResetLoading, setKYCResetLoading] = useState(false);
  const [kycReseted, setKYCReseted] = useState(false);
  const [countryCode, setCountryCode] = useState<string>('+971');
  const [customerData, setCustomerData] = useState<any>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showUpdatePhoneModal, setShowUpdatePhoneModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [baseCountryCode, setBaseCountryCode] = useState<string>('+971');
  const [updatedEmail, setUpdatedEmail] = useState('');
  const [updatedPhone, setUpdatedPhone] = useState('');
  const [saveEmail, setSaveEmail] = useState(false);
  const [savePhone, setSavePhone] = useState(false);

  const phoneNumber = baseCountryCode + phone;

  const [phoneUpdateStatus, setPhoneUpdateStatus] =
    useState<StatusType>('info');
  const [emailUpdateStatus, setEmailUpdateStatus] =
    useState<StatusType>('none');

  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(true);

  const [updatedPhoneData, setUpdatedPhoneData] = useState(
    {} as CusomersDTOResponseDataType
  );
  const [updatedPhoneLoading, setUpdatedPhoneLoading] = useState(false);

  const [updatedEmailData, setUpdatedEmailData] = useState(
    {} as CusomersDTOResponseDataType
  );
  const [updatedEmailLoading, setUpdatedEmailLoading] = useState(false);

  const [emailPollingIterations, setEmailPollingIterations] = useState(0);
  const [phonePollingIterations, setPhonePollingIterations] = useState(0);

  const isKYCDisabled = customerData?.kycRetryCount < 3;

  usePolling(
    setPhoneUpdateStatus,
    requestDelay,
    phonePollingIterations,
    updatedPhoneData.requestId
  );

  usePolling(
    setEmailUpdateStatus,
    requestDelay,
    emailPollingIterations,
    updatedEmailData.requestId
  );

  const onResetKYCAttempts = () => {
    if (customerId) {
      resetKYCAttempts(customerId, setKYCResetLoading, setKYCReseted);
      setShowResetModal(false);
    }
  };

  const customerDTO: CustomerDTOType = {
    token,
    type: 'EMAIL',
    cashewCustomerId: customerId,
    emailAddress: email,
    contactNo: phoneNumber,
    newValue: '',
  };

  const validateMobile = (value: string) => {
    const isMobile = /^[5]\d{8}$/;
    return isMobile?.test(value?.replace(/\s/g, ''));
  };

  const validateEmail = (value: string) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@\\"]+(\.[^<>()[\]\\.,;:\s@\\"]+)*)|(\\".+\\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(value?.trim());
  };

  const phoneFormatter = (value: string) =>
    value?.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3');

  const updateEmail = () => {
    updateCustomerData(
      {
        ...customerDTO,
        type: 'EMAIL',
        newValue: updatedEmail,
      },
      setUpdatedEmailLoading,
      setUpdatedEmailData,
      setEmailUpdateStatus,
      setShowUpdateEmailModal,
      setSaveEmail,
      setEmailPollingIterations,
      requestDelay,
      t,
      dispatch
    );
  };

  const updatePhone = () => {
    updateCustomerData(
      {
        ...customerDTO,
        type: 'CONTACT_NO',
        newValue: (countryCode + updatedPhone).replaceAll(' ', ''),
      },
      setUpdatedPhoneLoading,
      setUpdatedPhoneData,
      setPhoneUpdateStatus,
      setShowUpdatePhoneModal,
      setSavePhone,
      setPhonePollingIterations,
      requestDelay,
      t,
      dispatch,
      setBaseCountryCode
    );
  };

  useEffect(() => {
    if (customerId) {
      setLoading(true);
      api
        .get({
          endpoint: `customers/profile/${customerId}`,
        })
        .then((resp: any) => {
          setCountryCode(countryCode);
          const updatedObject = JSON.parse(JSON.stringify(resp?.data));
          updatedObject.mobileNumber = updatedObject.mobileNumber.replace(
            '+971',
            ''
          );
          setCustomerData(updatedObject);
          setLoading(false);
        })
        .catch((e: any) => {
          console.error(e);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [kycReseted]);

  useEffect(() => {
    setSaveEmail(isValidEmail && updatedEmail !== email);
  }, [updatedEmail, isValidEmail, email]);

  useEffect(() => {
    if (!phone) return;
    setSavePhone(
      isValidPhoneNumber &&
        (updatedPhone.replaceAll(' ', '') !== phone ||
          countryCode !== baseCountryCode)
    );
  }, [updatedPhone, countryCode, baseCountryCode, isValidPhoneNumber, phone]);

  useEffect(() => {
    setEmail(customerData?.email);
    setUpdatedEmail(customerData?.email);
    setPhone(customerData?.mobileNumber);
    setUpdatedPhone(customerData?.mobileNumber);
    setBaseCountryCode(countryCode);
  }, [customerData]);

  return (
    <div className={ns}>
      <Helmet>
        <title>{t('customerDetails.menuTitle')}</title>
      </Helmet>
      <PageWithNavigation showStores={false}>
        <Title className={`${ns}__title`}>
          {`${t('customerDetails.title')} ${customerId}`}
          <BackArrow
            to={`/${language}/customers`}
            title={t('customerDetails.backTitle')}
          />
        </Title>

        <Box>
          <>
            <Loading visible={loading} />
            {!loading && (
              <>
                <div className="row p-0">
                  <div className="col col-12 col-md-6">
                    <Input
                      placeholder={t('customerDetails.firstName')}
                      name="first-name"
                      value={customerData?.firstName ?? ''}
                      readonly
                    />
                  </div>
                  <div className="col col-12 col-md-6">
                    <Input
                      placeholder={t('customerDetails.lastName')}
                      name="lastname"
                      value={customerData?.lastName ?? ''}
                      readonly
                    />
                  </div>
                  <div className="col col-12 col-md-6">
                    <Input
                      placeholder={t('customerDetails.nationalId')}
                      name="national-id"
                      value={customerData?.idNumber ?? ''}
                      readonly
                    />
                  </div>
                  <div className="col col-12 col-md-6">
                    <Input
                      placeholder={t('customerDetails.dateOfBirth')}
                      name="date-of-birth"
                      value={
                        customerData?.dateOfBirth
                          ? dateFormat({
                              date: customerData?.dateOfBirth,
                              withTime: false,
                            })
                          : ''
                      }
                      readonly
                    />
                  </div>
                  <div className="col col-12 col-md-6">
                    <Input
                      placeholder={t('customerDetails.idExpiry')}
                      name="id-expiry"
                      value={
                        customerData?.idExpiry
                          ? dateFormat({
                              date: customerData?.idExpiry,
                              withTime: false,
                            })
                          : ''
                      }
                      readonly
                    />
                  </div>
                  <div className="col col-12 col-md-6">
                    <Input
                      placeholder={t('customerDetails.kycDate')}
                      name="kyc-date"
                      value={
                        customerData?.kycDate
                          ? dateFormat({
                              date: customerData?.kycDate,
                              withTime: true,
                            })
                          : ''
                      }
                      readonly
                    />
                  </div>

                  <div className="row mt-2">
                    <div className="col-12 col-md-6 col-lg-5">
                      <Input
                        placeholder={t('customerDetails.email')}
                        name="email"
                        value={updatedEmail}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setIsValidEmail(validateEmail(e.target.value));
                          setUpdatedEmail(e.target.value.replace(/\s/g, ''));
                        }}
                        error={emailError}
                        onBlur={() => {
                          setEmailError(
                            validateEmail(updatedEmail)
                              ? ''
                              : t('customerDetails.emailValidationError')
                          );
                        }}
                        onFocus={() => {
                          if (emailError === '') return;
                          setEmailError('');
                        }}
                        readonly={role === 'CUSTOMER_CARE'}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-5">
                      <Button
                        extraClass={`btn ${
                          saveEmail && !updatedEmailLoading ? 'blue' : 'grey'
                        } ${ns}__save-button`}
                        onClick={updateEmail}
                        disabled={
                          !saveEmail ||
                          updatedEmailLoading ||
                          role === 'CUSTOMER_CARE'
                        }
                        loading={updatedEmailLoading}
                      >
                        {t('customerDetails.saveButton')}
                      </Button>
                    </div>
                  </div>
                  <div className="row mt-2 mb-2 position-relative">
                    <div className="col-12 col-md-4 col-lg-2 country-code-dropdown">
                      <SelectComponent
                        label={t('customerDetails.contryCode')}
                        selected={countryCode}
                        data={currencyData}
                        onSelect={setCountryCode}
                        disabled={role === 'CUSTOMER_CARE'}
                      />
                    </div>
                    <div className="col-12 col-md-4 col-lg-3">
                      <Input
                        readonly={role === 'CUSTOMER_CARE'}
                        name="phoneNumber"
                        placeholder={t('customerDetails.phoneNumber')}
                        type="tel"
                        value={phoneFormatter(updatedPhone)}
                        maxLength={9}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          let value = e.target.value.replace(/\s/g, '');
                          value = value.replace(
                            /(\d{2})(\d{3})(\d{4})/,
                            '$1 $2 $3'
                          );
                          value = phoneFormatter(value);
                          setUpdatedPhone(value);
                          setIsValidPhoneNumber(validateMobile(value));
                        }}
                        error={mobileError}
                        onBlur={() => {
                          setMobileError(
                            validateMobile(updatedPhone)
                              ? ''
                              : t('customerDetails.phoneValidationError')
                          );
                        }}
                        onFocus={() => {
                          if (mobileError === '') return;
                          setMobileError('');
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-4 col-lg-2">
                      <Button
                        extraClass={`btn ${
                          savePhone && !updatedPhoneLoading ? 'blue' : 'grey'
                        } ${ns}__save-button`}
                        onClick={updatePhone}
                        disabled={
                          !savePhone ||
                          updatedPhoneLoading ||
                          role === 'CUSTOMER_CARE'
                        }
                        loading={updatedPhoneLoading}
                      >
                        {t('customerDetails.saveButton')}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className={`${ns}__block`}>
                  <div className={`${ns}__kyc-block`}>
                    <div className={`${ns}__kyc-text`}>
                      {t('customerDetails.kycAttempts')}
                      &nbsp;
                      <span className={`${ns}__kyc-value`}>
                        {customerData?.kycRetryCount}
                      </span>
                    </div>
                    <div className={`${ns}__kyc-btn`}>
                      <Button
                        extraClass={`btn ${isKYCDisabled ? 'grey' : 'blue'}`}
                        onClick={() => setShowResetModal(true)}
                        disabled={
                          isKYCDisabled ||
                          !(
                            (role === 'CUSTOMER_CARE' || role === 'CASHEW') &&
                            accessType === 'WRITE'
                          )
                        }
                      >
                        {t('customerDetails.resetButton')}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
            <ValidationModal
              status={emailUpdateStatus}
              type="email"
              updatedValue={updatedEmail}
              resendLink={updateEmail}
              updateStatus={setEmailUpdateStatus}
              showModal={showUpdateEmailModal}
              setShowModal={setShowUpdateEmailModal}
            />
            <ValidationModal
              status={phoneUpdateStatus}
              type="phone"
              updatedValue={countryCode + updatedPhone}
              resendLink={updatePhone}
              updateStatus={setPhoneUpdateStatus}
              showModal={showUpdatePhoneModal}
              setShowModal={setShowUpdatePhoneModal}
            />
            <ResetModal
              showConfirmModal={showResetModal}
              setShowConfirmModal={setShowResetModal}
              actionLoading={kycResetLoading}
              actionFunction={onResetKYCAttempts}
            />
          </>
        </Box>
      </PageWithNavigation>
    </div>
  );
};
